import { FC } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import useThemeState from '@/states/theme.state';
import { StepStatus } from '@/components/pages/dashboard/dashboard.types';

const BRAND_DARK = '#071753';
const IN_PROGRESS_ORANGE = '#FD6118';

interface StepNumberCircleProps {
  number: number;
  status: StepStatus;
}

// Renders the numbered circle used by the "Perancangan AOS" steps in the sidebar:
// a filled navy circle with a check badge when done, an orange-outlined circle
// with an orange number when in progress, or a light gray circle with a gray
// number when not yet started — mirroring the dashboard's step timeline.
export const StepNumberCircle: FC<StepNumberCircleProps> = ({ number, status }) => {
  const isDark = useThemeState(s => s.mode === 'dark');
  const doneBg = isDark ? IN_PROGRESS_ORANGE : BRAND_DARK;

  if (status === 'in_progress') {
    return (
      <span className="relative inline-flex shrink-0">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-lg border-2 bg-white text-sm font-bold dark:bg-gray-800"
          style={{ borderColor: IN_PROGRESS_ORANGE, color: IN_PROGRESS_ORANGE }}
        >
          {number}
        </span>
      </span>
    );
  }

  const done = status === 'done';

  return (
    <span className="relative inline-flex shrink-0">
      <span
        className={clsx(
          'flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold',
          done
            ? 'text-white'
            : 'bg-gray-100 text-gray-400 dark:bg-transparent dark:border dark:border-white dark:text-white',
        )}
        style={done ? { backgroundColor: doneBg } : undefined}
      >
        {number}
      </span>
      {done && (
        <span
          className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-white ring-2 ring-[#FAFAFA] dark:ring-gray-800"
          style={{ backgroundColor: doneBg }}
        >
          <Check size={10} strokeWidth={3.5} />
        </span>
      )}
    </span>
  );
};

export default StepNumberCircle;
